import { message, Modal, Progress, Typography } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { Agent } from '../../types';
import { UserContext } from '../../UserContext';
import { HWButton } from '../shared/StyledButton';

interface SendCalendarInviteModalProps {
  visible: boolean;
  onClose: () => void;
  selectedAgents: Agent[];
  apiUrl: string;
  clientId: string;
}

interface SendResult {
  agent: Agent;
  success: boolean;
  error?: string;
}

export function SendCalendarInviteModal({
  visible,
  onClose,
  selectedAgents,
  apiUrl,
  clientId,
}: SendCalendarInviteModalProps) {
  const user = useContext(UserContext);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentAgent, setCurrentAgent] = useState<string>('');
  const [results, setResults] = useState<SendResult[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setProgress(0);
      setCurrentAgent('');
      setResults([]);
    }
  }, [visible]);

  const handleSend = async () => {
    if (!user?.accessToken) {
      void message.error('No access token available');
      return;
    }

    if (selectedAgents.length === 0) {
      void message.error('No agents selected');
      return;
    }

    try {
      setSending(true);
      setProgress(0);
      setCurrentAgent('');
      setResults([]);

      const totalAgents = selectedAgents.length;
      const sendResults: SendResult[] = [];

      for (let i = 0; i < selectedAgents.length; i++) {
        const agent = selectedAgents[i];
        setCurrentAgent(`${agent.first_name} ${agent.last_name}`);
        setProgress(((i + 1) / totalAgents) * 100);

        try {
          const response = await fetch(
            `${apiUrl}/api/calendar/send-invitation`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.accessToken}`,
              },
              body: JSON.stringify({
                agent_id: agent.id,
                client_id: clientId,
              }),
            },
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.detail ||
                errorData.error ||
                errorData.message ||
                `Failed: ${response.statusText}`,
            );
          }

          sendResults.push({ agent, success: true });
        } catch (error) {
          console.error(
            `Error sending calendar invite to ${agent.first_name} ${agent.last_name}:`,
            error,
          );
          sendResults.push({
            agent,
            success: false,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      setResults(sendResults);
      setCurrentAgent('');

      const successCount = sendResults.filter(r => r.success).length;
      const errorCount = sendResults.filter(r => !r.success).length;

      if (successCount === totalAgents) {
        void message.success(
          `Successfully sent ${successCount} calendar invite${successCount > 1 ? 's' : ''}`,
        );
        onClose();
      } else if (successCount > 0) {
        void message.warning(
          `Sent ${successCount} invite${successCount > 1 ? 's' : ''}, ${errorCount} failed`,
        );
      } else {
        void message.error(
          `Failed to send calendar invites. ${errorCount} error${errorCount > 1 ? 's' : ''}`,
        );
      }
    } catch (error) {
      console.error('Error sending calendar invites:', error);
      void message.error('Failed to send calendar invites');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    if (!sending) {
      onClose();
    }
  };

  const failedResults = results.filter(r => !r.success);

  return (
    <Modal
      footer={null}
      onCancel={handleCancel}
      open={visible}
      title="Send Calendar Invites"
      width={500}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Typography.Paragraph>
          Send calendar invites to {selectedAgents.length} selected agent
          {selectedAgents.length > 1 ? 's' : ''}.
        </Typography.Paragraph>

        {sending && (
          <div>
            <Progress
              format={percent => `${Math.round(percent ?? 0)}%`}
              percent={progress}
              status="active"
            />
            {currentAgent && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                Sending invite to: {currentAgent}
              </div>
            )}
          </div>
        )}

        {failedResults.length > 0 && (
          <div
            style={{
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: 4,
              padding: 12,
            }}
          >
            <Typography.Text strong style={{ color: '#cf1322' }}>
              Failed invites:
            </Typography.Text>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {failedResults.map(r => (
                <li key={r.agent.id} style={{ color: '#cf1322', fontSize: 13 }}>
                  {r.agent.first_name} {r.agent.last_name}: {r.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <HWButton disabled={sending} onClick={handleCancel}>
            {failedResults.length > 0 ? 'Close' : 'Cancel'}
          </HWButton>
          {failedResults.length === 0 && !sending && (
            <HWButton
              loading={sending}
              onClick={() => {
                void handleSend();
              }}
              type="primary"
            >
              Send Invites
            </HWButton>
          )}
        </div>
      </div>
    </Modal>
  );
}
